import React, { useState, useEffect, createContext, useContext, useRef } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { debugLog, debugWarn } from '@/utils/logger'
import { isCookieConsentAccepted } from '@/components/CookieConsent'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  role: AccountRole | null
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<{ error: AuthError | null }>
  isOwner: boolean
  isSuperAdmin: boolean
  isDemoUser: boolean
}

type AccountRole = 'admin' | 'manager' | 'guest'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Move useAuth to separate export to avoid HMR issues with mixed exports
const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const useAuthProvider = () => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isOwner, setIsOwner] = useState(false)
  const [role, setRole] = useState<AccountRole | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [isDemoUser, setIsDemoUser] = useState(false)
  const authTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const tabId = useRef<string>(Math.random().toString(36).substring(7))
  const isInitialized = useRef<boolean>(false)
  const hasSetupEffect = useRef<boolean>(false)
  const queryClient = useQueryClient()

  // Helper to update auth state and sync across tabs
  const updateAuthState = (
    newUser: User | null,
    newSession: Session | null,
    newRole: AccountRole | null,
    newIsOwner: boolean,
    newIsSuperAdmin: boolean,
    newIsDemoUser: boolean,
    source: string
  ) => {
    debugLog(`🔐 useAuth: Tab ${tabId.current} - Updating auth state from: ${source}`)
    setUser(newUser)
    setSession(newSession)
    setRole(newRole)
    setIsOwner(newIsOwner)
    setIsSuperAdmin(newIsSuperAdmin)
    setIsDemoUser(newIsDemoUser)

    // Only broadcast to other tabs if consent is granted
    if (!isCookieConsentAccepted()) {
      return
    }

    // Ephemeral cross-tab sync: Write and immediately delete to trigger storage event
    // This notifies other tabs WITHOUT persisting auth data in localStorage
    try {
      const authState = {
        type: 'AUTH_STATE_UPDATE',
        user: newUser,
        session: newSession,
        role: newRole,
        isOwner: newIsOwner,
        isSuperAdmin: newIsSuperAdmin,
        isDemoUser: newIsDemoUser,
        tabId: tabId.current,
        timestamp: Date.now()
      }
      // Ephemeral: Set and immediately remove to trigger event without persisting
      localStorage.setItem('auth_state_sync', JSON.stringify(authState))
      localStorage.removeItem('auth_state_sync')
    } catch (error) {
      debugWarn('🔐 useAuth: Failed to broadcast auth state:', error)
    }
  }

  // Set loading complete
  const setLoadingComplete = (source: string) => {
    debugLog(`🔐 useAuth: Tab ${tabId.current} - Setting loading complete from: ${source}`)
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current)
      authTimeoutRef.current = null
    }
    setLoading(false)
  }

  // Check user role - fetches from Supabase users table
  const fetchAccountRole = async (user: User): Promise<{ role: AccountRole | null; isSuperAdmin: boolean }> => {
    try {
      debugLog('👤 useAuth: Checking user role for user:', user.id)

      const { data, error } = await supabase
        .from('users')
        .select('role, super_admin')
        .eq('id', user.id)
        .maybeSingle()

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('❌ useAuth: Error loading user role:', error)
        }
        return { role: 'guest', isSuperAdmin: false }
      }

      const resolvedRole = data?.role as AccountRole | null
      const superAdmin = data?.super_admin ?? false
      debugLog(`✅ useAuth: User role resolved - role: ${resolvedRole || 'guest'}, super_admin: ${superAdmin}`)
      return { role: resolvedRole ?? 'guest', isSuperAdmin: superAdmin }
    } catch (error) {
      console.error('❌ useAuth: Exception while loading account role:', error)
      return { role: 'guest', isSuperAdmin: false }
    }
  }

  // Initialize auth
  const initializeAuth = async () => {
    if (isInitialized.current) {
      debugLog(`🔐 useAuth: Tab ${tabId.current} - Already initialized, skipping`)
      return
    }

    try {
      debugLog(`🔐 useAuth: Tab ${tabId.current} - Initializing auth...`)
      isInitialized.current = true

      // Get session
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        console.error('❌ useAuth: Error getting session:', error)
        updateAuthState(null, null, null, false, false, false, 'session error')
      } else {
        debugLog('✅ useAuth: Session retrieved:', session ? 'authenticated' : 'not authenticated')

        let userRole: AccountRole | null = null
        let superAdmin = false
        const isDemo = session?.user?.email === 'demo@sergio.app'
        if (session?.user) {
          const roleData = await fetchAccountRole(session.user)
          userRole = roleData.role
          superAdmin = roleData.isSuperAdmin
        }

        const roleIsOwner = userRole === 'admin'
        updateAuthState(session?.user ?? null, session, userRole, roleIsOwner, superAdmin, isDemo, 'initial session')
      }
    } catch (error) {
      console.error('❌ useAuth: Error in initializeAuth:', error)
      updateAuthState(null, null, null, false, false, false, 'initialization error')
    } finally {
      setLoadingComplete('initialization complete')
    }
  }

  useEffect(() => {
    // Clear any stale logout flag from previous session
    sessionStorage.removeItem('logging_out')

    // Prevent double setup in React Strict Mode
    if (hasSetupEffect.current) {
      debugLog(`🔐 useAuth: Tab ${tabId.current} - Setup already called, skipping duplicate`)
      return
    }
    hasSetupEffect.current = true

    debugLog(`🔐 useAuth: Tab ${tabId.current} - Setting up auth system`)

    // Cross-tab sync listener (only if consent granted)
    const handleStorageChange = (e: StorageEvent) => {
      if (!isCookieConsentAccepted()) {
        return
      }

      if (e.key === 'auth_state_sync' && e.newValue) {
        try {
          const authState = JSON.parse(e.newValue)
          if (authState.type === 'AUTH_STATE_UPDATE' && authState.tabId !== tabId.current) {
            debugLog(`🔐 useAuth: Tab ${tabId.current} - Received auth state from tab ${authState.tabId}`)
            setUser(authState.user)
            setSession(authState.session)
            setRole(authState.role ?? null)
            setIsOwner(authState.isOwner)
            setIsSuperAdmin(authState.isSuperAdmin ?? false)
            setIsDemoUser(authState.isDemoUser ?? false)
            setLoadingComplete('cross-tab sync')
          }
        } catch (error) {
          debugWarn('🔐 useAuth: Failed to parse auth sync message:', error)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // Handle tab visibility changes - ensure loading doesn't get stuck
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && loading) {
        debugLog(`🔐 useAuth: Tab ${tabId.current} - Tab became visible while loading, forcing loading complete`)
        // Only intervene if loading is stuck - don't mess with valid auth state
        setLoadingComplete('visibility change - was stuck loading')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // REMOVED: Auth state caching to localStorage
    // Supabase already handles auth state persistence via its own tokens
    // We don't need to duplicate this data in localStorage

    // Only set timeout if we need to initialize
    if (!isInitialized.current) {
      authTimeoutRef.current = setTimeout(() => {
        if (!isInitialized.current) {
          debugWarn(`⚠️ useAuth: Tab ${tabId.current} - Auth initialization timeout, forcing complete`)
          setLoadingComplete('timeout')
        }
      }, 8000) // Reduced to 8 seconds
    }

    // Initialize auth for this tab
    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        debugLog(`🔐 useAuth: Tab ${tabId.current} - Auth state changed:`, event)

        // If we're in the middle of logging out, skip state updates
        // This prevents the component tree from re-rendering with null state before redirect
        if (sessionStorage.getItem('logging_out') === 'true') {
          debugLog('🚪 useAuth: Skipping auth state update during logout')
          return
        }

        // Clear user-specific cache on sign out or user change
        if (event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
          debugLog('🗑️ useAuth: Clearing user-specific cache due to auth change')

          // Clear React Query cache immediately
          queryClient.clear()
          debugLog('🗑️ Cleared React Query cache')

          // Clear localStorage
          try {
            const localStorageKeys = Object.keys(localStorage)
            localStorageKeys.forEach(key => {
              if (key.includes('react-query') || key.includes('tanstack') ||
                  key.includes('user_preferences') || key.includes('settings')) {
                localStorage.removeItem(key)
              }
            })
          } catch (error) {
            debugWarn('⚠️ Error clearing user cache on auth change:', error)
          }
        }

        let userRole: AccountRole | null = null
        let superAdmin = false
        const isDemo = session?.user?.email === 'demo@sergio.app'
        if (session?.user) {
          const roleData = await fetchAccountRole(session.user)
          userRole = roleData.role
          superAdmin = roleData.isSuperAdmin
        }

        const roleIsOwner = userRole === 'admin'
        updateAuthState(session?.user ?? null, session, userRole, roleIsOwner, superAdmin, isDemo, `auth event: ${event}`)
        setLoadingComplete(`auth event: ${event}`)

        // REMOVED: Auth state caching
        // Supabase manages auth persistence internally - no need to duplicate
      }
    )

    return () => {
      debugLog(`🔐 useAuth: Tab ${tabId.current} - Cleaning up auth`)
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current)
      }
      subscription.unsubscribe()
      hasSetupEffect.current = false
    }
  }, [])

  // Inactivity timeout management
  useEffect(() => {
    if (!session?.user) return

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000 // 30 minutes
    const WARNING_TIME = 25 * 60 * 1000 // Show warning at 25 minutes (5 min before logout)
    let timeoutPausedAt: number | null = null
    let warningTimeoutRef: NodeJS.Timeout | null = null

    const showInactivityWarning = () => {
      // Only show warning if page is visible
      if (document.visibilityState === 'hidden') {
        return
      }

      // Dispatch custom event for warning modal
      const event = new CustomEvent('inactivity-warning', {
        detail: {
          timeRemaining: 5 * 60 * 1000, // 5 minutes in ms
          onExtend: () => {
            debugLog('🔄 useAuth: User extended session from warning')
            resetInactivityTimeout()
          }
        }
      })
      window.dispatchEvent(event)
    }

    const resetInactivityTimeout = () => {
      // Only reset if page is visible
      if (document.visibilityState === 'hidden') {
        return
      }

      // Clear existing timeouts
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
      }
      if (warningTimeoutRef) {
        clearTimeout(warningTimeoutRef)
      }

      lastActivityRef.current = Date.now()

      // Set warning timeout (5 minutes before logout)
      warningTimeoutRef = setTimeout(() => {
        if (document.visibilityState === 'visible') {
          showInactivityWarning()
        }
      }, WARNING_TIME)

      // Set logout timeout (30 minutes)
      inactivityTimeoutRef.current = setTimeout(() => {
        // Only sign out if page is currently visible
        if (document.visibilityState === 'visible') {
          debugLog(`🕐 useAuth: Tab ${tabId.current} - Inactivity timeout, signing out`)
          signOut()
        }
      }, INACTIVITY_TIMEOUT)
    }

    const handleActivity = () => {
      resetInactivityTimeout()
    }

    const handleInactivityVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Pause the timeout when tab becomes hidden
        if (inactivityTimeoutRef.current) {
          clearTimeout(inactivityTimeoutRef.current)
        }
        if (warningTimeoutRef) {
          clearTimeout(warningTimeoutRef)
        }
        timeoutPausedAt = Date.now()
        debugLog(`⏸️ useAuth: Pausing inactivity timeout - tab hidden`)
      } else {
        // Resume/reset timeout when tab becomes visible
        debugLog(`▶️ useAuth: Resuming inactivity timeout - tab visible`)
        resetInactivityTimeout()
      }
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    document.addEventListener('visibilitychange', handleInactivityVisibilityChange)

    // Only start timeout if page is currently visible
    if (document.visibilityState === 'visible') {
      resetInactivityTimeout()
    }

    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
      }
      if (warningTimeoutRef) {
        clearTimeout(warningTimeoutRef)
      }
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
      document.removeEventListener('visibilitychange', handleInactivityVisibilityChange)
    }
  }, [session?.user])

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      return { error }
    } catch (err) {
      console.error('❌ useAuth: signIn request failed:', err)
      const message = err instanceof Error ? err.message : 'Unable to reach authentication service'
      const friendlyMessage = message.includes('Failed to fetch')
        ? 'Unable to reach the authentication service. Check your Supabase configuration and network connection.'
        : message
      return {
        error: {
          name: 'AuthError',
          message: friendlyMessage,
          status: 0
        } as AuthError
      }
    }
  }

  const signOut = async () => {
    debugLog('🚪 useAuth: Signing out and clearing all user data...')

    // CRITICAL: Set a flag to prevent rendering during logout
    sessionStorage.setItem('logging_out', 'true')

    // Immediately redirect to root domain login page to prevent UI lag
    // Determine redirect URL
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    const loginUrl = isDev
      ? '/login'
      : 'https://sergio.app/login'

    // Start cleanup in background (don't await)
    const cleanup = async () => {
      // Clear React Query cache
      queryClient.clear()
      debugLog('🗑️ Cleared React Query cache during sign out')

      // Clear all localStorage (except core preferences like language, theme, consent)
      try {
        const localStorageKeys = Object.keys(localStorage)
        const keysToKeep = ['sergio-cookie-consent', 'i18nextLng', 'sergio-theme-mode']

        localStorageKeys.forEach(key => {
          // Skip keys that should persist across sessions
          if (keysToKeep.includes(key)) {
            return
          }

          // Clear any Supabase auth-related keys
          if (key.startsWith('sb-')) {
            localStorage.removeItem(key)
          }
          // Clear dev company slug
          if (key === 'dev_company_slug') {
            localStorage.removeItem(key)
          }
          // Clear any legacy cache keys (React Query, auth state, etc.)
          if (key.includes('react-query') || key.includes('tanstack') ||
              key.includes('auth_state') || key.includes('user_preferences') ||
              key.includes('settings') || key.includes('preferences')) {
            localStorage.removeItem(key)
          }
        })
      } catch (error) {
        debugWarn('⚠️ Error clearing localStorage:', error)
      }

      // Clear session storage
      sessionStorage.clear()

      // Sign out from Supabase with GLOBAL scope to clear all sessions
      try {
        await supabase.auth.signOut({ scope: 'global' })
        debugLog('✅ Supabase global signOut complete')
      } catch (err) {
        debugWarn('⚠️ Error during signOut:', err)
      }
    }

    // Start cleanup but don't wait
    cleanup()

    // Redirect immediately for better UX
    debugLog('🔀 Redirecting to:', loginUrl)
    window.location.href = loginUrl

    return { error: null }
  }

  return {
    user,
    session,
    loading,
    role,
    signIn,
    signOut,
    isOwner,
    isSuperAdmin,
    isDemoUser
  }
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authValue = useAuthProvider()

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Export useAuth with components for HMR compatibility - FIXED!
export { useAuth }
